import * as THREE from 'three';
import { EffectComposer, EffectPass, RenderPass, SMAAEffect, SMAAImageLoader, SMAAPreset, ToneMappingEffect, ToneMappingMode } from 'postprocessing';
import { N8AOPostPass } from 'n8ao';
import { DEFAULT_AO_SETTINGS, validateAOSettings } from './ambientOcclusion.js';

let imagesPromise, smaaImages;
export function loadAntialiasing() {
  if (!imagesPromise) imagesPromise = new SMAAImageLoader().load(() => {}).then((images) => { smaaImages = images; })
    .catch((error) => { imagesPromise = undefined; throw error; });
  return imagesPromise;
}

/** One camera projection type per pipeline; export uses its own full-size pipeline. */
export function createDoorPostprocessing(renderer, scene, camera, { ambientOcclusion = false, antialias = 'smaa', quality = 'Medium', accumulate = false, aoSettings = DEFAULT_AO_SETTINGS } = {}) {
  if (antialias !== 'msaa' && !smaaImages) throw new Error('Antialiasing textures are not ready yet.');
  const size = renderer.getSize(new THREE.Vector2());
  const autoClear = renderer.autoClear;
  const composer = new EffectComposer(renderer, {
    frameBufferType: THREE.HalfFloatType,
    // Avoid multiplying 4K HDR buffers by four for large batch exports.
    multisampling: size.x * size.y * renderer.getPixelRatio() ** 2 <= 4194304
      ? Math.min(4, renderer.capabilities.maxSamples || 0) : 0
  });
  renderer.autoClear = autoClear;
  composer.addPass(new RenderPass(scene, camera));
  const ao = ambientOcclusion ? new N8AOPostPass(scene, camera, size.x, size.y) : null;
  let appliedSettings, appliedQuality;
  const setAOSettings = (settings = DEFAULT_AO_SETTINGS, nextQuality = quality) => {
    if (!ao || (settings === appliedSettings && nextQuality === appliedQuality)) return;
    validateAOSettings(settings);
    ao.configuration.aoRadius = settings.radiusMm / 1000;
    ao.configuration.distanceFalloff = settings.distanceFalloff;
    ao.configuration.intensity = settings.intensity;
    if (nextQuality !== appliedQuality) ao.setQualityMode(nextQuality);
    appliedSettings = settings; appliedQuality = nextQuality;
  };
  if (ao) {
    setAOSettings(aoSettings);
    ao.configuration.accumulate = accumulate; // Only a frozen export accumulates; moving doors must not leave trails.
    ao.configuration.gammaCorrection = false;
    // Explicitly initialize every depth-dependent pass for the export's orthographic camera.
    ao.configureAOPass(ao.configuration.depthBufferType, camera.isOrthographicCamera);
    ao.configureDenoisePass(ao.configuration.depthBufferType, camera.isOrthographicCamera);
    ao.configureEffectCompositer(ao.configuration.depthBufferType, camera.isOrthographicCamera);
    composer.addPass(ao);
  }
  // Offscreen rendering is linear HDR; apply the same ACES exposure as direct rendering.
  composer.addPass(new EffectPass(camera, new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC })));
  if (antialias !== 'msaa') composer.addPass(new EffectPass(camera,
    new SMAAEffect(smaaImages[0], smaaImages[1], SMAAPreset.ULTRA)));
  return {
    cameraType: camera.type,
    setAOSettings,
    render(nextCamera = camera) {
      composer.setMainCamera(nextCamera);
      if (ao) ao.camera = nextCamera;
      const previousAutoClear = renderer.autoClear;
      renderer.autoClear = false;
      try { composer.render(); } finally { renderer.autoClear = previousAutoClear; }
    },
    resize(width, height) { composer.setSize(width, height, false); },
    async renderForExport(signal) {
      // Ultra uses 64 AO samples per frame. Accumulate the full 1024-sample result
      // before encoding; elapsed time alone does not make an AO image converge.
      const frames = ao && accumulate ? 16 : 2;
      for (let frame = 0; frame < frames; frame++) {
        if (signal?.aborted) throw new DOMException('Rendering cancelled.', 'AbortError');
        this.render();
        if (frame % 4 === 3 && frame < frames - 1) await new Promise((resolve) => setTimeout(resolve, 0));
      }
    },
    dispose() {
      // N8AO inherits a shallow Pass.dispose(), which skips its fullscreen quads.
      Object.values(ao || {}).forEach((value) => {
        if (value?.material?.isShaderMaterial && typeof value.dispose === 'function') value.dispose();
      });
      composer.dispose();
    }
  };
}
